import * as THREE from 'three';

export default function calculateSlicePerimeter(mesh: THREE.Mesh, height: number): number {

    // Update the world matrix of the mesh to ensure accurate calculations
    mesh.updateMatrixWorld();

    // 1. Define the slicing plane
    const slicePlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), height);

    const geometry = mesh.geometry;

    // Check geometry type
    if (!geometry.isBufferGeometry) {
        console.log("This function only supports BufferGeometry.");
        return 0;
    }
    const positionAttribute = geometry.attributes.position as THREE.BufferAttribute;
    const indexAttribute = geometry.index as THREE.BufferAttribute;

    if (!positionAttribute) {
        console.log("This function requires the model to have a 'position' attribute.");
        return 0;
    }
    if (!indexAttribute) {
        console.log("This function only supports Indexed Meshes.");
        return 0;
    }

    const segments: THREE.Line3[] = [];
    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();




    // 2. Calculate all intersection segments
    for (let i = 0; i < indexAttribute.count; i += 3) {
        vA.fromBufferAttribute(positionAttribute, indexAttribute.getX(i));
        vB.fromBufferAttribute(positionAttribute, indexAttribute.getX(i + 1));
        vC.fromBufferAttribute(positionAttribute, indexAttribute.getX(i + 2));

        // Convert vertices to world coordinates
        vA.applyMatrix4(mesh.matrixWorld);
        vB.applyMatrix4(mesh.matrixWorld);
        vC.applyMatrix4(mesh.matrixWorld);

        const intersectionPoints: THREE.Vector3[] = [];
        const lineAB = new THREE.Line3(vA, vB);
        const lineBC = new THREE.Line3(vB, vC);
        const lineCA = new THREE.Line3(vC, vA);



        // 3. Check intersections with the slicing plane
        const target = new THREE.Vector3();
        if (slicePlane.intersectLine(lineAB, target)) {
            intersectionPoints.push(target.clone());
        }
        if (slicePlane.intersectLine(lineBC, target)) {
            intersectionPoints.push(target.clone());
        }
        if (slicePlane.intersectLine(lineCA, target)) {
            if (intersectionPoints.length < 2 || intersectionPoints[0].distanceTo(target) > 1e-6) {
                intersectionPoints.push(target.clone());
            }
        }

        if (intersectionPoints.length === 2) {
            segments.push(new THREE.Line3(intersectionPoints[0], intersectionPoints[1]));
        }
    }

    if (segments.length === 0) {
        return 0; // There are no intersection segments
    }




    // 4. Stitch segments into every closed loop they form (a double-walled mesh produces
    // an inner and outer ring; we need both so we can pick the outer one below).
    const loops: THREE.Line3[][] = [];
    const remaining: THREE.Line3[] = [...segments];

    while (remaining.length > 0) {
        const path: THREE.Line3[] = [];
        const firstSegment = remaining.pop();
        if (!firstSegment) break;
        path.push(firstSegment);

        while (remaining.length > 0) {
            const lastPoint = path[path.length - 1].end;
            let foundNext = false;
            for (let i = 0; i < remaining.length; i++) {
                const currentSegment = remaining[i];
                if (currentSegment.start.distanceTo(lastPoint) < 1e-6) {
                    path.push(remaining.splice(i, 1)[0]);
                    foundNext = true;
                    break;
                } else if (currentSegment.end.distanceTo(lastPoint) < 1e-6) {
                    const reversed = new THREE.Line3(currentSegment.end, currentSegment.start);
                    path.push(reversed);
                    remaining.splice(i, 1);
                    foundNext = true;
                    break;
                }
            }
            if (!foundNext) break;
        }
        loops.push(path);
    }

    if (loops.length === 0) return 0;

    // 5. Return the perimeter of the loop with the largest XZ enclosed area —
    // that's the outer ring even when the silhouette is non-convex.
    let bestPerimeter = 0;
    let bestArea = -1;

    for (const loop of loops) {
        let signedArea2 = 0;
        for (const line of loop) {
            signedArea2 += line.start.x * line.end.z - line.end.x * line.start.z;
        }
        const area = Math.abs(signedArea2) * 0.5;

        let perimeter = 0;
        for (const line of loop) {
            perimeter += line.start.distanceTo(line.end);
        }
        perimeter += loop[loop.length - 1].end.distanceTo(loop[0].start);

        if (area > bestArea) {
            bestArea = area;
            bestPerimeter = perimeter;
        }
    }

    return bestPerimeter;
}